import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { ArrowLeft, Users, Calendar, BarChart3, Settings, MapPin, Clock, GraduationCap } from 'lucide-react';
import Tabs from '../../components/common/Tabs';

// Import tabs
import ClassStudentsSettings from '../tabs/settings/ClassStudentsSettings';
import ClassScheduleSettings from '../tabs/settings/ClassScheduleSettings';
import ClassAttendanceTab from '../tabs/ClassAttendanceTab';

// Mock class data (in real app, fetch by classId)
const mockClassesData = {
  '1': {
    id: 1,
    className: 'Adult Beginner Tennis',
    description: 'Perfect for adults new to tennis. Learn basic strokes and court positioning.',
    sportTypeId: 5,
    sportName: 'Tennis',
    skillLevel: 'beginner',
    coachId: 101,
    coachName: 'Sarah Johnson',
    coachImage: 'https://i.pravatar.cc/150?img=1',
    recurrence: 'Mon/Wed/Fri',
    time: '18:00',
    duration: 90,
    enrolled: 15,
    maxCapacity: 20,
    facility: 'Court A',
    enrollmentFee: 150.00,
    enrollmentDueDate: '2026-03-01',
    startDate: '2026-03-01',
    endDate: '2026-05-31',
    createdBy: 1,
    academyId: '1',
    academyName: 'Elite Tennis Academy'
  }
};

const ClassDetailsPage = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('students');

  const classData = mockClassesData[classId] || mockClassesData['1'];
  const userRole = 'coach'; // In real app, get from auth context

  const tabs = [
    { id: 'students', label: 'Roster', shortLabel: 'Roster', icon: Users, badge: classData.enrolled },
    { id: 'schedule', label: 'Schedule', shortLabel: 'Schedule', icon: Calendar },
    { id: 'attendance', label: 'Attendance', shortLabel: 'Attendance', icon: BarChart3 }
  ];

  // Add settings tab for admin
  if (userRole === 'admin' || userRole === 'owner') {
    tabs.push({ id: 'settings', label: 'Settings', shortLabel: 'Settings', icon: Settings });
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'students':
        return <ClassStudentsSettings classId={classId} classData={classData} userRole={userRole} />;
      case 'schedule':
        return <ClassScheduleSettings classId={classId} classData={classData} />;
      case 'attendance':
        return <ClassAttendanceTab classId={classId} classData={classData} />;
      case 'settings':
        return <div className={`${theme.card} p-6 rounded-xl`}>Class Settings (Coming Soon)</div>;
      default:
        return <ClassStudentsSettings classId={classId} classData={classData} userRole={userRole} />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return 'green';
      case 'intermediate': return 'yellow';
      case 'advanced': return 'red';
      default: return 'gray';
    }
  };

  return (
    <div className={`${theme.background} min-h-screen`}>
      {/* Header */}
      <div className={`${theme.card} border-b`}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Back Button */}
          <button
            onClick={() => navigate(`/academies/${classData.academyId}`)}
            className={`flex items-center gap-x-2 ${theme.link} mb-4 hover:underline`}
          >
            <ArrowLeft size={20} />
            <span>Back to {classData.academyName}</span>
          </button>

          {/* Class Header Info */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Left: Class Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className={`text-3xl font-bold ${theme.text}`}>{classData.className}</h1>
                <span className={`px-3 py-1 bg-${getLevelColor(classData.skillLevel)}-100 text-${getLevelColor(classData.skillLevel)}-700 rounded-lg text-sm font-semibold`}>
                  {classData.skillLevel}
                </span>
              </div>
              <p className={`${theme.textSecondary} mb-4`}>{classData.description}</p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-x-2">
                  <GraduationCap size={18} className={theme.textSecondary} />
                  <div>
                    <div className={`text-sm ${theme.textSecondary}`}>Coach</div>
                    <div className="font-semibold">{classData.coachName}</div>
                  </div>
                </div>
                <div className="flex items-center gap-x-2">
                  <Calendar size={18} className={theme.textSecondary} />
                  <div>
                    <div className={`text-sm ${theme.textSecondary}`}>Schedule</div>
                    <div className="font-semibold">{classData.recurrence}</div>
                  </div>
                </div>
                <div className="flex items-center gap-x-2">
                  <Clock size={18} className={theme.textSecondary} />
                  <div>
                    <div className={`text-sm ${theme.textSecondary}`}>Time</div>
                    <div className="font-semibold">{classData.time}</div>
                  </div>
                </div>
                <div className="flex items-center gap-x-2">
                  <MapPin size={18} className={theme.textSecondary} />
                  <div>
                    <div className={`text-sm ${theme.textSecondary}`}>Facility</div>
                    <div className="font-semibold">{classData.facility}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Enrollment Stats */}
            <div className={`${theme.card} p-4 rounded-xl border ${theme.border} min-w-[200px]`}>
              <div className="text-center mb-3">
                <div className={`text-3xl font-bold ${theme.text}`}>{classData.enrolled}/{classData.maxCapacity}</div>
                <div className={`text-sm ${theme.textSecondary}`}>Students Enrolled</div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${(classData.enrolled / classData.maxCapacity) * 100}%` }}
                />
              </div>
              <div className={`text-xs text-center ${theme.textSecondary}`}>
                {classData.maxCapacity - classData.enrolled} spots remaining
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className={`${theme.card} border-b sticky top-14 z-40`}>
        <div className="max-w-7xl mx-auto px-4">
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            showMobileMenu={true}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ClassDetailsPage;
